import StickyScrollbar from 'components/StickyScrollbar';
import PropTypes from 'prop-types';
import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { stepType } from 'utils/reactFlowUtils';

import Branches from './branches';
import Node from './node';
import { useSelection } from './selectionProvider';

export default function Flowchart({
  type,
  treesData,
  root,
  expandTo = undefined,
}) {
  const projectId = useMemo(() => root.projectId, [root]);
  const flatTrees = useMemo(() => treesData.flat(2), [treesData]);
  const flowchartRef = useRef();
  const nodeRefs = useRef({});
  const nodeData = useRef({});
  const treesStatus = useRef(treesData.map((tree) => tree.map(() => [])));
  const [updateWidth, setUpdateWidth] = useState(false);
  const [updatePaths, setUpdatePaths] = useState(0);
  const { setSelectAll, selectedRange } = useSelection();

  useEffect(() => {
    if (!nodeRefs.current) return () => {};

    const resizeObserver = new ResizeObserver(() => {
      setUpdateWidth((prev) => !prev);
    });
    Object.keys(nodeRefs.current).forEach((uniqueId) => {
      resizeObserver.observe(nodeRefs.current[uniqueId]);
    });

    return () => resizeObserver.disconnect();
  }, [type]);

  const triggerUpdatePaths = useCallback(() => {
    setUpdatePaths((prev) => prev + 1);
  }, []);

  useEffect(() => {
    flatTrees.forEach((node) => {
      const nodeRef = nodeRefs.current[node.uniqueId];
      const nodeDatum = {
        width: nodeRef.getBoundingClientRect().width,
        offsetLeft: nodeRef.offsetLeft,
        childSteps: node.childSteps,
      };
      if (nodeData.current[node.parentStep]?.childSteps.length === 1) {
        // ensure single child nodes are at least as wide as parent node
        const parentNodeWidth = nodeData.current[node.parentStep].width;
        nodeRef.style.minWidth = `${parentNodeWidth}px`;
        nodeDatum.width = Math.max(
          nodeDatum.width,
          parentNodeWidth,
        );
      }
      nodeData.current[node.uniqueId] = nodeDatum;
    });
    triggerUpdatePaths();
  }, [flatTrees, updateWidth, triggerUpdatePaths]);

  const updateTreesStatus = useCallback((status, treeIdx, levelIdx, uniqueId) => {
    if (!treesStatus.current[treeIdx]) {
      treesStatus.current[treeIdx] = [];
    }
    if (!treesStatus.current[treeIdx][levelIdx]) {
      treesStatus.current[treeIdx][levelIdx] = [];
    }
    treesStatus.current[treeIdx][levelIdx][uniqueId] = status;
  }, []);

  return treesData?.length > 0
    ? (
      <>
        <div className={`flowchart ${type}`} ref={flowchartRef}>
          <ul>
            {treesData.map((tree, treeIdx) => (
              // eslint-disable-next-line react/no-array-index-key
              <li key={`${projectId}-tree${treeIdx}`} className="treeContainer">
                <ol className="tree">
                  {tree.map((level, levelIdx) => (
                    // eslint-disable-next-line react/no-array-index-key
                    <li className="level" key={`${projectId}-level${levelIdx}`}>
                      {levelIdx !== 0 && (
                        <Branches
                          type={type}
                          level={level}
                          levelStatus={treesStatus.current?.[treeIdx]?.[levelIdx]}
                          nodeData={nodeData}
                          updatePaths={updatePaths}
                        />
                      )}
                      <ol className="nodes">
                        {level.map((node) => (
                          <Node
                            ref={(ref) => {
                              nodeRefs.current[node.uniqueId] = ref;
                            }}
                            type={type}
                            node={node}
                            levelIdx={levelIdx}
                            expandTo={expandTo}
                            updatePaths={triggerUpdatePaths}
                            updateNodeStatus={
                              (status) => updateTreesStatus(
                                status,
                                treeIdx,
                                levelIdx,
                                node.uniqueId,
                              )
                            }
                            key={node.uniqueId}
                          />
                        ))}
                      </ol>
                    </li>
                  ))}
                </ol>
              </li>
            ))}
          </ul>
        </div>
        {type === 'expanded' && (
          <div className="stickyFooter">
            <StickyScrollbar scrollRef={flowchartRef} />
            <div className="selectedRange">
              <svg className="icon" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 30 10">
                <circle r="5" cx="5" cy="5" />
                <line x1="5" x2="25" y1="5" y2="5" />
                <circle r="5" cx="25" cy="5" />
              </svg>
              <ul>
                <li>
                  Cycles:
                  <span className="emphasize">
                    {selectedRange.cycles === -1 ? '-' : selectedRange.cycles}
                  </span>
                </li>
                <li>
                  Average press time:
                  <span className="emphasize">
                    {selectedRange.averageProcessTime === -1 ? '-' : `${selectedRange.averageProcessTime} min `}
                  </span>
                </li>
                <li>
                  Average gap time:
                  <span className="emphasize">
                    {selectedRange.avgGap === -1 ? '-' : `${selectedRange.avgGap} min`}
                  </span>
                </li>
                <li>
                  Estimate:
                  <span className="emphasize">
                    {selectedRange.estimate === -1 ? '-' : `${selectedRange.estimate} min`}
                  </span>
                </li>
                <li>
                  Yield:
                  <span className="emphasize">
                    {selectedRange.yieldRate === -1 ? '-' : `${selectedRange.yieldRate}%`}
                  </span>
                </li>
              </ul>
              <button
                type="button"
                className="selectAll"
                onClick={() => setSelectAll(true)}
              >
                <svg xmlns="http://www.w3.org/2000/svg" width="22" height="15" viewBox="0 0 22 15">
                  <polyline points="14.5 2.5 19.5 7.5 14.5 12.5" />
                  <line x1="21.5" y1=".5" x2="21.5" y2="14.5" />
                  <polyline points="7.5 12.5 2.5 7.5 7.5 2.5" />
                  <line x1=".5" y1="14.5" x2=".5" y2=".5" />
                  <line x1="2.5" y1="7.5" x2="19.5" y2="7.5" />
                </svg>
                Select all
              </button>
            </div>
          </div>
        )}
      </>
    ) : (
      <p className="emptyCollapsedView">No steps</p>
    );
}

Flowchart.propTypes = {
  type: PropTypes.oneOf(['collapsed', 'expanded']).isRequired,
  treesData: PropTypes.arrayOf(
    PropTypes.arrayOf(
      PropTypes.arrayOf(stepType),
    ),
  ).isRequired,
  root: stepType.isRequired,
  expandTo: PropTypes.func,
};
